// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what the Monday Madness email includes and how it looks. Task lists switch on and
// off across every project at once; the layout can be kept for the following Mondays.

import {SectionKindIcon} from '@app/features/konvo_reports/components/MondaySectionBlock';
import {moveEntry} from '@app/features/konvo_reports/utils/MondayDraftEdit';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {ActionButton, IconButton, Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {
	type MondayDraftSection,
	type MondayReportDraft,
	mondaySectionKey,
} from '@konvo/schema/src/domains/konvo/MondayReportDraft';
import {useLingui} from '@lingui/react/macro';
import {IconArrowDown, IconArrowUp, IconEye, IconEyeOff, IconTemplate} from '@tabler/icons-react';
import styles from './MondayReportCard.module.css';

const ICON = {size: 16, stroke: 1.75} as const;

/** One row per kind of project section (Completed, In review, ...), in first-seen order. */
function projectSectionKinds(
	draft: MondayReportDraft,
): Array<{key: string; section: MondayDraftSection; hidden: boolean}> {
	const rows = new Map<string, {key: string; section: MondayDraftSection; hidden: boolean}>();
	for (const project of draft.projects) {
		for (const section of project.sections) {
			const key = mondaySectionKey(section);
			const row = rows.get(key);
			if (row) row.hidden = row.hidden && section.hidden;
			else rows.set(key, {key, section, hidden: section.hidden});
		}
	}
	return [...rows.values()];
}

export function MondayReportCustomise({
	draft,
	update,
	busy,
	onSaveLayout,
	onForgetLayout,
}: {
	draft: MondayReportDraft;
	update: (change: Partial<MondayReportDraft>) => void;
	busy: boolean;
	onSaveLayout: () => void;
	onForgetLayout: () => void;
}) {
	const {t} = useLingui();
	const kinds = projectSectionKinds(draft);
	const setKindHidden = (key: string, hidden: boolean) =>
		update({
			projects: draft.projects.map((project) => ({
				...project,
				sections: project.sections.map((section) =>
					mondaySectionKey(section) === key ? {...section, hidden} : section,
				),
			})),
		});
	return (
		<div className={styles.customise} data-flx="konvo-monday.customise">
			<div className={styles.customiseColumn}>
				<h4>{t`Email`}</h4>
				<div className={styles.customiseRow}>
					<span>{t`Appearance`}</span>
					<Segmented<'light' | 'dark'>
						compact
						value={draft.palette}
						onChange={(palette) => update({palette})}
						options={[
							{value: 'light', children: t`Light`},
							{value: 'dark', children: t`Dark`},
						]}
					/>
				</div>
				<Switch
					compact
					label={t`Summary tiles`}
					description={t`Task counts and hours under the heading.`}
					value={draft.options.summary}
					onChange={(summary) => update({options: {...draft.options, summary}})}
				/>
				<Switch
					compact
					label={t`Hours next to tasks`}
					description={t`Time logged per task and per project.`}
					value={draft.options.hours}
					onChange={(hours) => update({options: {...draft.options, hours}})}
				/>
			</div>
			<div className={styles.customiseColumn}>
				<h4>{t`In every project`}</h4>
				{kinds.length === 0 && <p className={styles.customiseHint}>{t`No project lists this week.`}</p>}
				<ul className={styles.outline}>
					{kinds.map((row) => (
						<li key={row.key} data-hidden={row.hidden || undefined}>
							<SectionKindIcon kind={row.section.kind} />
							<span className={styles.outlineName}>{row.section.title || t`Untitled`}</span>
							<IconButton
								className={styles.tinyIcon}
								label={row.hidden ? t`Show in the email` : t`Hide from the email`}
								icon={row.hidden ? <IconEyeOff {...ICON} /> : <IconEye {...ICON} />}
								active={row.hidden}
								onClick={() => setKindHidden(row.key, !row.hidden)}
							/>
						</li>
					))}
				</ul>
				<h4>{t`Report sections`}</h4>
				<ul className={styles.outline}>
					{draft.sections.map((section, index) => (
						<li key={section.id} data-hidden={section.hidden || undefined}>
							<SectionKindIcon kind={section.kind} />
							<span className={styles.outlineName}>{section.title || t`Untitled`}</span>
							<IconButton
								className={styles.tinyIcon}
								label={t`Move section up`}
								icon={<IconArrowUp {...ICON} />}
								disabled={index === 0}
								onClick={() => update({sections: moveEntry(draft.sections, index, -1)})}
							/>
							<IconButton
								className={styles.tinyIcon}
								label={t`Move section down`}
								icon={<IconArrowDown {...ICON} />}
								disabled={index === draft.sections.length - 1}
								onClick={() => update({sections: moveEntry(draft.sections, index, 1)})}
							/>
							<IconButton
								className={styles.tinyIcon}
								label={section.hidden ? t`Show in the email` : t`Hide from the email`}
								icon={section.hidden ? <IconEyeOff {...ICON} /> : <IconEye {...ICON} />}
								active={section.hidden}
								onClick={() =>
									update({
										sections: draft.sections.map((entry, i) =>
											i === index ? {...entry, hidden: !entry.hidden} : entry,
										),
									})
								}
							/>
						</li>
					))}
				</ul>
			</div>
			<div className={styles.customiseFooter}>
				<p className={styles.customiseHint}>
					{t`Keep these sections, their order and these settings for the next Mondays.`}
				</p>
				<ActionButton small tone="quiet" disabled={busy} onClick={onForgetLayout}>
					{t`Use standard layout`}
				</ActionButton>
				<ActionButton small icon={<IconTemplate {...ICON} />} disabled={busy} onClick={onSaveLayout}>
					{t`Remember this layout`}
				</ActionButton>
			</div>
		</div>
	);
}
